import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import { Field, FieldCreateDTO } from '../../../types/fields';
import { DefaultValueInputComponent } from '../../../components/default-value-input/default-value-input.component';
import { FieldPickerComponent } from './field-picker.component';

export type DialogMode = 'create' | 'edit' | 'clone';

export interface FieldDialogData {
  mode: DialogMode;
  field?: Field;
  allFields?: Field[];
}

export interface FieldDialogResult {
  action: 'save' | 'cancel';
  data?: FieldCreateDTO;
  fieldId?: string;
}

@Component({
  selector: 'app-field-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    TextFieldModule,
    DefaultValueInputComponent,
    FieldPickerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-dialog.component.html',
  styles: [`
    :host {
      display: block;
      height: 100%;
    }

    /* Custom spinner color for submit button */
    mat-spinner ::ng-deep circle {
      stroke: white !important;
    }
  `]
})
export class FieldDialogComponent {
  private dialogRef = inject(MatDialogRef<FieldDialogComponent>);
  private data = inject<FieldDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);

  submitting = signal(false);
  enumValuesArray = signal<string[]>(this.data.field?.enumValues || []);
  enumInputValue = signal('');

  fieldTypes = [
    { value: 'string', label: 'String' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'string-list', label: 'String List' },
    { value: 'number-list', label: 'Number List' },
    { value: 'object', label: 'Object' },
    { value: 'object-list', label: 'Object List' }
  ];

  selectedFieldIds = signal<string[]>(this.data.field?.fieldIds || []);
  selectedType = signal<string>(this.data.field?.type || 'string');

  categoryInputValue = signal('');

  distinctCategories = computed(() =>
    [...new Set(this.data.allFields?.map(f => f.category).filter(Boolean))].sort()
  );

  filteredCategories = computed(() => {
    const input = this.categoryInputValue().toLowerCase();
    if (!input) return this.distinctCategories();
    return this.distinctCategories().filter(c => c.toLowerCase().includes(input));
  });

  form = this.fb.group({
    name: [this.getInitialName(), [Validators.required]],
    category: [this.data.field?.category || '', [Validators.required]],
    anchorField: [this.data.field?.category === 'anchor'],
    type: [this.data.field?.type || 'string', [Validators.required]],
    description: [this.data.field?.description || ''],
    required: [this.data.field?.required ?? false],
    isAlwaysVisible: [this.data.field?.isAlwaysVisible ?? true],
    minTolerance: [
      this.data.field?.minTolerance ?? 0,
      [Validators.min(0), Validators.max(1), maxTwoDecimalsValidator]
    ],
    enumValues: [this.formatEnumValues(this.data.field?.enumValues)],
    defaultValue: [this.data.field?.defaultValue ?? null]
  });

  isObjectType = computed(() => {
    const type = this.selectedType();
    return type === 'object' || type === 'object-list';
  });

  /** Fields available for the picker — exclude the field being edited to prevent self-reference */
  pickerFields = computed(() => {
    const all = this.data.allFields || [];
    if (this.data.mode === 'edit' && this.data.field) {
      return all.filter(f => f.id !== this.data.field!.id);
    }
    return all;
  });

  constructor() {
    this.form.get('type')?.valueChanges.subscribe((type) => {
      this.selectedType.set(type ?? 'string');
      if (type !== 'object' && type !== 'object-list') {
        this.selectedFieldIds.set([]);
      }
      if (this.form.get('defaultValue')?.value !== null) {
        this.form.patchValue({ defaultValue: null }, { emitEvent: false });
      }
    });

    // Keep `anchorField` toggle in sync with `category`.
    // The toggle is a UI-only convenience: BE only stores `category`.
    this.form.get('category')?.valueChanges.subscribe((category) => {
      const isAnchor = (category ?? '').trim() === 'anchor';
      if (this.form.get('anchorField')?.value !== isAnchor) {
        this.form.patchValue({ anchorField: isAnchor }, { emitEvent: false });
      }
    });

    // Anchor fields have their category locked to 'anchor' — make the coupling explicit in the UI.
    if (this.form.get('anchorField')?.value) {
      this.form.get('category')?.disable({ emitEvent: false });
    }
  }

  toggleAnchorField(): void {
    const current = this.form.get('anchorField')?.value;
    const next = !current;
    this.form.patchValue({ anchorField: next }, { emitEvent: false });
    const categoryControl = this.form.get('category');
    if (next) {
      categoryControl?.setValue('anchor');
      categoryControl?.disable({ emitEvent: false });
    } else {
      categoryControl?.enable({ emitEvent: false });
      if ((categoryControl?.value ?? '').trim() === 'anchor') {
        categoryControl?.setValue('');
      }
      // minTolerance only makes sense for anchor fields; reset when disabling.
      this.form.patchValue({ minTolerance: 0 });
    }
    categoryControl?.markAsTouched();
  }


  dialogTitle = computed(() => {
    switch (this.data.mode) {
      case 'create': return 'Add New Field';
      case 'edit': return 'Edit Field';
      case 'clone': return 'Clone Field';
    }
  });

  submitButtonText = computed(() => {
    switch (this.data.mode) {
      case 'create': return 'Create Field';
      case 'edit': return 'Save Changes';
      case 'clone': return 'Clone Field';
    }
  });

  showEnumValues = computed(() => {
    const type = this.selectedType();
    return type === 'string' || type === 'string-list';
  });


  toggleRequired(): void {
    const current = this.form.get('required')?.value;
    this.form.patchValue({ required: !current });
  }

  toggleAlwaysVisible(): void {
    const current = this.form.get('isAlwaysVisible')?.value;
    this.form.patchValue({ isAlwaysVisible: !current });
  }

  onEnumInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.enumInputValue.set(input.value);
  }

  onEnumKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const val = this.enumInputValue().trim();
      if (val && !this.enumValuesArray().includes(val)) {
        this.enumValuesArray.update(arr => [...arr, val]);
        this.enumInputValue.set('');
      }
    } else if (event.key === 'Backspace' && !this.enumInputValue() && this.enumValuesArray().length > 0) {
      this.enumValuesArray.update(arr => arr.slice(0, -1));
    }
  }

  removeEnumValue(tag: string): void {
    this.enumValuesArray.update(arr => arr.filter(t => t !== tag));
  }

  onFieldIdsChange(ids: string[]): void {
    this.selectedFieldIds.set(ids);
  }

  private getInitialName(): string {
    if (!this.data.field) return '';
    if (this.data.mode === 'clone') return `${this.data.field.name}_copy`;
    return this.data.field.name;
  }

  private formatEnumValues(values?: string[]): string {
    return values?.join(', ') || '';
  }


  onSubmit(): void {
    if (this.form.invalid) return;

    // getRawValue: the category control is disabled while the field is an anchor,
    // but its locked value ('anchor') must still be submitted.
    const formValue = this.form.getRawValue();
    const type = formValue.type!;

    const isObject = type === 'object' || type === 'object-list';

    const isAnchor = (formValue.category ?? '').trim() === 'anchor';
    const dto: FieldCreateDTO = {
      name: formValue.name!,
      category: formValue.category!.trim(),
      type: type as Field['type'],
      description: formValue.description || undefined,
      required: formValue.required ?? false,
      isAlwaysVisible: formValue.isAlwaysVisible ?? true,
      // minTolerance is only meaningful for anchor fields; omit otherwise.
      minTolerance: isAnchor ? (formValue.minTolerance ?? 0) : undefined,
      enumValues: this.enumValuesArray().length > 0 ? this.enumValuesArray() : undefined,
      defaultValue: isObject ? null : (formValue.defaultValue ?? null) as Field['defaultValue'],
      fieldIds: isObject ? this.selectedFieldIds() : undefined
    };

    const result: FieldDialogResult = {
      action: 'save',
      data: dto,
      fieldId: this.data.mode === 'edit' ? this.data.field?.id : undefined
    };

    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' } as FieldDialogResult);
  }
}

/**
 * Validator: ensure a numeric value has at most 2 decimal digits.
 * Empty/null/undefined values are considered valid (handled by min/max + required if set).
 */
export function maxTwoDecimalsValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return { maxTwoDecimals: true };
  // Use a small epsilon to avoid floating-point issues like 0.1 * 100 = 10.000000000000002
  const scaled = num * 100;
  if (Math.abs(scaled - Math.round(scaled)) > 1e-9) {
    return { maxTwoDecimals: true };
  }
  return null;
}
